import React, { ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Chip,
  IconButton,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { sortByRank, reorderItems } from '../utils/ranking';

interface DraggableItem {
  rank: number;
}

interface DraggableItemsPanelProps<T extends DraggableItem> {
  title: string;
  titleIcon: ReactNode;
  subtitle: string;
  items: T[];
  emptyStateIcon: ReactNode;
  emptyStateText: string;
  emptyStateSubtext: string;
  onReorderItems: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  getItemId: (item: T) => string;
  droppableId: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  headerExtra?: ReactNode;
}

function DraggableItemsPanel<T extends DraggableItem>({
  title,
  titleIcon,
  subtitle,
  items,
  emptyStateIcon,
  emptyStateText,
  emptyStateSubtext,
  onReorderItems,
  renderItem,
  getItemId,
  droppableId,
  collapsed = false,
  onToggleCollapse,
  headerExtra,
}: DraggableItemsPanelProps<T>) {
  const sortedItems = sortByRank(items);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const { source, destination } = result;

    if (source.index === destination.index) {
      return;
    }

    const updatedItems = reorderItems(sortedItems, source.index, destination.index);
    onReorderItems(updatedItems);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {titleIcon}
              {title}
              <Chip
                label={items.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Typography>
            {onToggleCollapse && (
              <IconButton size="small" onClick={onToggleCollapse}>
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Box>
          {!collapsed && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {!collapsed && headerExtra && (
            <Box sx={{ mt: 1 }}>
              {headerExtra}
            </Box>
          )}
        </Box>

        {!collapsed && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Droppable droppableId={droppableId}>
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ p: 1 }}
                >
                {sortedItems.map((item, index) => {
                  const draggableId = getItemId(item);
                  return (
                  <Draggable key={draggableId} draggableId={draggableId} index={index}>
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={undefined}
                        sx={{
                          p: 0,
                          mb: 1,
                          transform: provided.draggableProps.style?.transform,
                          transition: provided.draggableProps.style?.transition,
                        }}
                      >
                        <Paper
                          elevation={snapshot.isDragging ? 4 : 1}
                          sx={{
                            width: '100%',
                            p: 2,
                            border: '1px solid',
                            borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                            bgcolor: 'background.paper',
                            transition: 'all 0.2s ease',
                            opacity: snapshot.isDragging ? 0.8 : 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                mt: -0.5,
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                '&:active': { cursor: 'grabbing' },
                              }}
                            >
                              <DragIcon fontSize="small" />
                            </Box>

                            {renderItem(item, index)}
                          </Box>
                        </Paper>
                      </ListItem>
                    )}
                  </Draggable>
                  );
                })}

                {provided.placeholder}

                {sortedItems.length === 0 && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      color: 'text.secondary',
                    }}
                  >
                    {emptyStateIcon}
                    <Typography variant="body2">
                      {emptyStateText}
                    </Typography>
                    <Typography variant="caption">
                      {emptyStateSubtext}
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Droppable>
        </Box>
        )}
      </Paper>
    </DragDropContext>
  );
}

export default DraggableItemsPanel;